// Vermögensverwalter-Giganten: BlackRock, Vanguard, State Street, Blackstone, Apollo, KKR
// Manuell gepflegt — bei Update-Befehl von Claude auf Basis der Quartalszahlen bearbeitet

export type TrendSignal = 'bullish' | 'neutral' | 'bearish'

export interface Gigant {
  id:        string
  name:      string
  ticker:    string
  typ:       'ETF/Index' | 'Alternative'
  aum:       string   // verwaltetes Vermögen ('' = nicht ausgewiesen)
  aumAsOf:   string
  kennzahl:  string   // wichtigste aktuelle Zahl
  fokus:     string   // wohin das Haus gerade strategisch drückt
  signal:    TrendSignal
  bullets:   string[]
}

export interface GigantenTrend {
  id:       string
  titel:    string
  signal:   TrendSignal
  kern:     string    // 1-Satz-Kernaussage
  belege:   string[]  // harte Zahlen
  bedeutung: string   // was heisst das für Privatanleger
}

export interface Positionierung {
  anlage:   string
  view:     'Übergewichten' | 'Neutral' | 'Untergewichten'
  aenderung: 'hoch' | 'runter' | 'neu' | 'unverändert'
  notiz:    string
}

export interface Ruecknahme {
  fonds:     string
  manager:   string
  anfragePct: number  // Rücknahmewünsche Q3 in % des Fonds
}

export interface GigantenData {
  version:         string
  updatedAt:       string
  zusammenfassung: string
  giganten:        Gigant[]
  trends:          GigantenTrend[]
  biiStand:        string
  positionierung:  Positionierung[]
  ruecknahmen:     Ruecknahme[]
  ruecknahmeLimitPct: number
  etfMarkt:        Array<{ label: string; value: string; note: string }>
  quellen:         Array<{ title: string; url: string }>
}

export const giganten: GigantenData = {
  version:   '2026-10-16',
  updatedAt: '2026-10-16',

  zusammenfassung: 'Die Giganten verdienen an Rekordzuflüssen — und werden gleichzeitig vorsichtiger. BlackRock sammelt $205 Mrd. in einem Quartal ein, vor allem in Anleihen und ETFs, und stuft US-Aktien taktisch auf Neutral. Die Alternative-Manager setzen voll auf KI-Infrastruktur, kämpfen aber in ihren Private-Credit-Fonds für Privatanleger mit gedeckelten Rücknahmen.',

  giganten: [
    {
      id: 'blackrock', name: 'BlackRock', ticker: 'BLK', typ: 'ETF/Index',
      aum: '$15.5 Bio.', aumAsOf: '2026-09-30',
      kennzahl: '$205 Mrd. Nettozuflüsse Q3 · $869 Mrd. über 12 Monate',
      fokus: 'ETFs, Anleihen, Private Markets, KI-Infrastruktur, Tokenisierung',
      signal: 'bullish',
      bullets: [
        'Umsatz $7.75 Mrd. (+19%), bereinigtes EPS $15.93 (+38%), $550 Mio. Aktienrückkäufe',
        'iShares-ETFs $135 Mrd. Zuflüsse, aktive Strategien $66 Mrd., Cash $31 Mrd.',
        'Nach Anlageklasse: Fixed Income $78.2 Mrd., Aktien $52.4 Mrd., Alternatives $24.2 Mrd., Digital Assets $7.9 Mrd.',
        'Private-Markets-AUM $336 Mrd., $68 Mrd. in 12 Monaten eingesammelt',
        'AI Infrastructure Partnership (bis $100 Mrd.): erste Investition abgeschlossen',
        'Fink: Ein Moratorium für Rechenzentren würde die USA in eine Rezession stürzen',
      ],
    },
    {
      id: 'vanguard', name: 'Vanguard', ticker: 'privat', typ: 'ETF/Index',
      aum: '~$12 Bio.', aumAsOf: '2026',
      kennzahl: 'VOO grösster ETF der Welt, +13% seit Jahresbeginn',
      fokus: 'Günstige Indexfonds, aktive Anleihen, vorsichtige Öffnung für Krypto',
      signal: 'neutral',
      bullets: [
        'Seit Dezember 2025 Zugang zu Krypto-ETFs von Drittanbietern für über 50 Mio. Brokerage-Kunden',
        'Sucht ersten Head of Digital Assets — eigener Bitcoin-ETF aber weiterhin nicht geplant',
        'CEO Salim Ramji: Niedrige Kosten geben aktiven Managern langfristig einen Vorsprung, vor allem bei Anleihen',
        'Bleibt bei Private Markets zurückhaltend — Fokus auf liquide, günstige Produkte',
      ],
    },
    {
      id: 'statestreet', name: 'State Street', ticker: 'STT', typ: 'ETF/Index',
      aum: '$6.3 Bio.', aumAsOf: '2026-09-30',
      kennzahl: 'Rekord-ETF-Vermögen $2.3 Bio., Rekord-ETF-Zuflüsse im Q3',
      fokus: 'SPDR-ETFs, Verwahrung, Datenplattform',
      signal: 'bullish',
      bullets: [
        'State Street Investment Management mit Rekord-AUM von $6.3 Bio.',
        'ETF-Vermögen Rekord $2.3 Bio. — profitiert direkt vom ETF-Superzyklus',
        'Erwartet für US-ETFs 2026 Zuflüsse von $2.3 Bio. (bisheriger Rekord: $1.52 Bio. in 2025)',
      ],
    },
    {
      id: 'blackstone', name: 'Blackstone', ticker: 'BX', typ: 'Alternative',
      aum: '$1.35 Bio.', aumAsOf: '2026-06-30',
      kennzahl: 'Rechenzentren-Portfolio ~$180–185 Mrd. · Q3-Zahlen am 22. Okt',
      fokus: 'Rechenzentren, Energie/Strom, Frontier-KI-Firmen, Private Credit',
      signal: 'neutral',
      bullets: [
        'Q2: Rekord-AUM $1.35 Bio. (+11%), ausschüttbare Gewinne $2 Mrd. (+26%)',
        'Schwarzman: KI-Investitionen (Rechenzentren, Energie, KI-Firmen) sind der grösste Ergebnistreiber',
        'BCRED (Private Credit): Rücknahmewünsche ~10% im Q3, Netto-Abflüsse ~3%',
        'Partner der NVIDIA-Finanzierungsplattform für KI-Rechenleistung (>$500 Mrd.)',
      ],
    },
    {
      id: 'apollo', name: 'Apollo', ticker: 'APO', typ: 'Alternative',
      aum: '', aumAsOf: '',
      kennzahl: 'Apollo Debt Solutions: 14.7% Rücknahmewünsche, Limit 5%',
      fokus: 'Private Credit, Versicherung, KI-Infrastruktur-Finanzierung',
      signal: 'bearish',
      bullets: [
        '$26-Mrd.-Fonds Apollo Debt Solutions: 14.7% der Anteile zur Rücknahme angemeldet (Vorquartal 16.8%)',
        'Rücknahmen auf 5% gedeckelt — erwartete Netto-Abflüsse ~$500 Mio. (3% NAV)',
        'Finanziert $15-Mrd.-KI-Infrastrukturprojekt in Japan',
        'Partner der NVIDIA-Finanzierungsplattform',
      ],
    },
    {
      id: 'kkr', name: 'KKR', ticker: 'KKR', typ: 'Alternative',
      aum: '', aumAsOf: '',
      kennzahl: 'K-Income Trust: nur 2.5% Rücknahmewünsche',
      fokus: 'KI-Infrastruktur, Private Credit, Infrastruktur',
      signal: 'neutral',
      bullets: [
        'Neue KI-Infrastruktur-Gesellschaft: Samsung sagt $1 Mrd. zu',
        'Private-Credit-Fonds K-Income (EMEA/APAC) mit nur 2.5% Rücknahmewünschen — deutlich stabiler als US-Peers',
        'Partner der NVIDIA-Finanzierungsplattform',
      ],
    },
  ],

  trends: [
    {
      id: 'etf-superzyklus',
      titel: 'ETF-Superzyklus: Rekord schon im September gebrochen',
      signal: 'bullish',
      kern: 'US-ETFs haben bis Ende September mehr Zuflüsse als in jedem vollen Jahr zuvor — die Index-Giganten sind die grössten Gewinner.',
      belege: [
        'US-ETF-Zuflüsse bis Ende Sep: $1.54 Bio. (Rekord 2025: $1.52 Bio.)',
        'Prognose State Street für 2026: $2.3 Bio.',
        'Aktien-ETFs >$1 Bio., Anleihen-ETFs >$469 Mrd.',
        'iShares allein: $135 Mrd. im Q3',
      ],
      bedeutung: 'Günstige, liquide ETFs bleiben der beste Baustein für Privatanleger. Die Gebühren sinken durch den Wettbewerb weiter.',
    },
    {
      id: 'flucht-in-rendite',
      titel: 'Flucht in Rendite: Anleihen sind zurück',
      signal: 'bullish',
      kern: 'Bei 10Y-Renditen um 5% fliesst das meiste neue Geld bei BlackRock in Anleihen — und das hauseigene Research-Institut übergewichtet kurze Treasuries erstmals seit sechs Jahren.',
      belege: [
        'BlackRock Fixed-Income-Zuflüsse Q3: $78.2 Mrd. (grösster Block)',
        'BII: kurze Treasuries Übergewichten, lange Neutral (vorher Untergewichten)',
        '10Y erreichte Anfang Oktober 5.25%, getrieben durch Realzinsen',
        '~75bp Puffer gegen weitere Zinsanstiege laut BII',
      ],
      bedeutung: 'Kurze bis mittlere Anleihen (2–5 Jahre) bieten hohe Rendite bei begrenztem Risiko. Lange Laufzeiten bleiben schwankungsanfällig.',
    },
    {
      id: 'ki-infrastruktur',
      titel: 'KI-Infrastruktur wird zur eigenen Anlageklasse',
      signal: 'neutral',
      kern: 'Alle Giganten finanzieren den KI-Ausbau — Rechenzentren, Strom, Chips. Das ist ihr grösster Wachstumsmotor, aber auch ihr grösstes neues Risiko.',
      belege: [
        'NVIDIA-Plattform mit Apollo, BlackRock, Blackstone, Brookfield, Goldman, KKR: >$500 Mrd. Drittkapital',
        'BlackRock AI Infrastructure Partnership: bis $100 Mrd.',
        'Blackstone-Rechenzentren: ~$180–185 Mrd. Wert',
        'Gegenwind: ≥100 US-Gemeinden mit Baustopp, CoreWeave-CDS 643bp',
      ],
      bedeutung: 'Der KI-Boom wird zunehmend mit Fremdkapital bezahlt. Wenn die Finanzierung stockt, trifft es Chips, Utilities und die Alternative-Manager gleichzeitig. Fink selbst warnt vor einer Rezession bei einem Baustopp.',
    },
    {
      id: 'private-credit-stress',
      titel: 'Private Credit: Privatanleger wollen raus',
      signal: 'bearish',
      kern: 'Die grossen semi-liquiden Private-Credit-Fonds erfüllen weniger als die Hälfte der Rücknahmewünsche — die Liquiditätsversprechen haben einen Haken.',
      belege: [
        'Rücknahmewünsche Q3: Apollo 14.7%, Ares 13.1%, BlackRock 11.5%, Blackstone 10%',
        'Alle vier deckeln Rücknahmen bei 5% pro Quartal',
        'Druck leicht rückläufig (Apollo Vorquartal 16.8%)',
        'Aktien von Apollo, Blue Owl, Blackstone schwächer trotz nachlassender Ängste',
      ],
      bedeutung: 'Semi-liquide Fonds sind nicht täglich handelbar. In Stressphasen kommt man nicht raus. Für Privatanleger: liquide Anleihen-ETFs statt Private-Credit-Vehikel.',
    },
    {
      id: 'tokenisierung',
      titel: 'Tokenisierung & Digital Assets werden Mainstream',
      signal: 'bullish',
      kern: 'BlackRock macht Tokenisierung zum Kernthema, selbst Vanguard öffnet sich für Krypto-ETFs.',
      belege: [
        'BlackRock Digital-Asset-Zuflüsse Q3: $7.9 Mrd.',
        'IBIT: >$65 Mrd. kumulierte Zuflüsse seit Start — im Oktober aber schwankend',
        'Fink-Jahresbrief 2026: Tokenisierung vergleichbar mit den Anfängen des Internets',
        'Vanguard: Krypto-ETF-Zugang seit Dez. 2025, sucht Head of Digital Assets',
      ],
      bedeutung: 'Bitcoin-ETFs sind etabliert, bleiben aber volatil (BTC unter $81 000 am 15. Okt). Höchstens als kleine Beimischung.',
    },
    {
      id: 'aktiv-und-plattform',
      titel: 'Aktive ETFs und Plattform-Geschäft wachsen',
      signal: 'neutral',
      kern: 'Die Giganten verdienen zunehmend an aktiven Strategien, Technologie und Daten — nicht mehr nur an billigen Indexfonds.',
      belege: [
        'BlackRock aktive Strategien: $66 Mrd. Zuflüsse im Q3',
        'BlackRock Technology Services (Aladdin): $588 Mio. Umsatz (+14%)',
        'Mehr aktive als passive ETFs am Markt (seit Mitte 2025)',
      ],
      bedeutung: 'Aktive ETFs können bei Anleihen sinnvoll sein. Bei Aktien bleiben günstige Indexfonds meist die bessere Wahl.',
    },
  ],

  biiStand: '2026-10-12',
  positionierung: [
    { anlage: 'US-Aktien',                 view: 'Neutral',        aenderung: 'runter',      notiz: 'Von Übergewichten — kurzfristiges Risikomanagement, KI/Tech innerhalb weiter bevorzugt' },
    { anlage: 'US-Treasuries kurz',        view: 'Übergewichten',  aenderung: 'neu',         notiz: 'Rendite sichern — erstes Upgrade nach sechs Jahren Untergewichtung' },
    { anlage: 'US-Treasuries lang',        view: 'Neutral',        aenderung: 'hoch',        notiz: '10Y 5.25%, getrieben durch Realzinsen; ~75bp Puffer' },
    { anlage: 'Emerging Markets Aktien',   view: 'Übergewichten',  aenderung: 'unverändert', notiz: 'Beibehalten' },
    { anlage: 'Japanische Staatsanleihen', view: 'Untergewichten', aenderung: 'hoch',        notiz: 'Untergewichtung reduziert — JGB 10Y bei ~3%' },
    { anlage: 'US Agency MBS',             view: 'Neutral',        aenderung: 'unverändert', notiz: '—' },
  ],

  ruecknahmen: [
    { fonds: 'Apollo Debt Solutions', manager: 'Apollo',     anfragePct: 14.7 },
    { fonds: 'Flaggschiff-Fonds',     manager: 'Ares',       anfragePct: 13.1 },
    { fonds: 'Flaggschiff-Fonds',     manager: 'BlackRock',  anfragePct: 11.5 },
    { fonds: 'BCRED',                 manager: 'Blackstone', anfragePct: 10.0 },
    { fonds: 'K-Income Trust',        manager: 'KKR',        anfragePct: 2.5  },
  ],
  ruecknahmeLimitPct: 5,

  etfMarkt: [
    { label: 'US-ETF-Zuflüsse YTD',   value: '$1.54 Bio.', note: 'bis Ende Sep — bereits Allzeitrekord' },
    { label: 'Prognose 2026',          value: '$2.3 Bio.',  note: 'State Street Investment Management' },
    { label: 'BlackRock Q3-Zuflüsse',  value: '$205 Mrd.',  note: 'davon iShares $135 Mrd.' },
    { label: 'State Street ETF-AUM',   value: '$2.3 Bio.',  note: 'Rekord zum Quartalsende' },
  ],

  quellen: [
    { title: 'Pulse 2.0: BlackRock Q3 2026 — AUM $15.5 Bio., $205 Mrd. Zuflüsse', url: 'https://pulse2.com/blackrock-q3-2026-results/' },
    { title: 'TipRanks: BlackRock Posts Strong Q3 2026 Earnings and Inflows', url: 'https://www.tipranks.com/news/company-announcements/blackrock-posts-strong-q3-2026-earnings-and-inflows' },
    { title: 'CNBC: Fink warnt vor Rechenzentren-Moratorium', url: 'https://www.cnbc.com/2026/10/14/blackrocks-fink-warns-data-center-moratorium-could-spark-a-recession.html' },
    { title: 'BlackRock Investment Institute: Upgrading U.S. Treasuries to lock in income (12. Okt)', url: 'https://www.blackrock.com/corporate/insights/blackrock-investment-institute/global-weekly-commentary' },
    { title: 'BNN Bloomberg/Reuters: US-ETFs auf Kurs für Rekordzuflüsse (State Street)', url: 'https://www.bnnbloomberg.ca/investing/etfs/2026/10/02/us-listed-etfs-on-course-for-record-annual-inflows-state-street-says/' },
    { title: 'Benzinga: State Street Q3 2026 Earnings Call', url: 'https://www.benzinga.com/news/26/10/62361764/state-street-q3-2026-earnings-call-transcript' },
    { title: 'Commercial Observer: Blackstone Q2 — Rekord-AUM durch Rechenzentren', url: 'https://commercialobserver.com/2026/07/blackstone-reports-second-quarter-earnings-data-centers-ai/' },
    { title: 'Yahoo Finance: Apollos $26-Mrd.-Private-Credit-Fonds — erneuter Ansturm', url: 'https://finance.yahoo.com/markets/options/articles/apollo-26-billion-private-credit-200010936.html' },
    { title: 'Traders Union: Private-Credit-Rücknahmedruck lässt nach', url: 'https://tradersunion.com/news/financial-news/show/3519422-private-credit-funds-redemption-eases/' },
    { title: 'GlobeNewswire: NVIDIA-Plattform mit Apollo, BlackRock, Blackstone, Brookfield, Goldman, KKR', url: 'https://www.globenewswire.com/news-release/2026/08/10/3342187/0/en/nvidia-partners-with-apollo-blackrock-blackstone-brookfield-goldman-sachs-and-kkr-to-establish-ai-compute-infrastructure-financing-platforms-to-mobilize-over-500-billion-of-third-p.html' },
    { title: 'Bloomberg: Apollo unterstützt $15-Mrd.-KI-Projekt in Japan', url: 'https://www.bloomberg.com/news/articles/2026-10-01/apollo-to-support-15-billion-ai-infrastructure-project-in-japan' },
    { title: 'etf.com: Vanguard-CEO zu Krypto-ETFs', url: 'https://www.etf.com/sections/features/vanguard-ceo-throws-cold-water-crypto-etf-speculation' },
    { title: 'Pensions & Investments: Vanguard öffnet sich für Krypto-ETFs', url: 'https://www.pionline.com/asset-management/exchange-traded-funds/pi-vanguard-crypto-etf-third-party-brokerage-401k/' },
  ],
}
